import { createChart } from 'lightweight-charts';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2 } from '../../reusableComponents/Loader';
import { fetchCandlestickData } from '../../store/reducers/candleStickReducer';

const CandleStickChart = ({ symbol, setPositionInfo, candleStickTime, selectedDate, period, positions, color, activeTimeFrame, setRenderInfo }) => {
  const chartContainerRef = useRef();
  const { chartData: candleLightData, loading } = useSelector(state => state.candlestickChart);
  const { AccDetails } = useSelector(state => state.dashboard);
  const [data, setData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const idToken = useSelector((state) => state.ACCtoken);

  const dispatch = useDispatch();

  useEffect(() => {
    const updatedData = candleLightData?.map(item => ({
      ...item,
      datetime: item?.date
    }));
    setData(updatedData);
  }, [candleLightData]);

  useEffect(() => {
    if (!symbol) return;
    const specificTimes = Object.values(positions).flat().map(dateTime => dateTime);

    dispatch(fetchCandlestickData({
      symbol,
      candleStickTime,
      platform: AccDetails?.platform,
      period,
      setChartLoading,
      idToken,
      specificTimes,
      activeTimeFrame,
      setRenderInfo
    }));
  }, [symbol, dispatch, candleStickTime, period, activeTimeFrame]);

  useEffect(() => {
    if (chartLoading) return;

    const chart = createChart(chartContainerRef?.current, {
      width: chartContainerRef?.current?.clientWidth,
      height: 500,
      layout: {
        background: { color: '#1e1e1e' },
        textColor: '#d1d4dc',
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { visible: false },
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: '#444',
      },
      crosshair: {
        mode: 1,
      },
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderUpColor: '#26a69a',
      borderDownColor: '#ef5350',
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
    });

    const sortedData = [...data]
      .sort((a, b) => new Date(a.datetime) - new Date(b.datetime))
      .filter((value, index, self) =>
        index === 0 || new Date(value.datetime).getTime() !== new Date(self[index - 1].datetime).getTime()
      );

    const formattedData = sortedData.map(item => ({
      time: Math.floor(new Date(item.datetime).getTime() / 1000), // ✅ SAFE and simplified
      open: item.open,
      high: item.high,
      low: item.low,
      close: item.close,
    }));

    candleSeries.setData(formattedData);

    const specificTimes = Object.values(positions).flat();
    const allSpecificTimes = [...specificTimes].sort((a, b) => new Date(a) - new Date(b));
    const firstTime = allSpecificTimes[0];

    let firstPoint = null;
    let linePoints = [];

    // Handle Single Marker
    if (firstTime && allSpecificTimes.length === 1) {
      const firstTimestamp = Math.floor(new Date(firstTime).getTime() / 1000);
      firstPoint = formattedData.find(d => d.time === firstTimestamp);

      if (firstPoint) {
        candleSeries.setMarkers([
          {
            time: firstPoint.time,
            position: 'inBar',
            color: 'blue',
            shape: 'circle',
            text: '',
          },
        ]);

        const padding = 60 * 60 * 3;
        chart.timeScale().setVisibleRange({
          from: firstPoint.time - padding,
          to: firstPoint.time + padding,
        });
      }
    }

    // Handle Line Between Two Points
    linePoints = specificTimes.map(time => {
      const timestamp = Math.floor(new Date(time).getTime() / 1000);
      return formattedData.find(d => d.time === timestamp);
    }).filter(Boolean);

    if (linePoints.length === 2) {
      const isProfit = linePoints[1].close > linePoints[0].close;

      const lineSeries = chart.addLineSeries({
        color: color,
        lineWidth: 2,
        lineStyle: 0,
      });

      lineSeries.setData([
        {
          time: linePoints[0].time,
          value: linePoints[0].close,
        },
        {
          time: linePoints[1].time,
          value: linePoints[1].close,
        }
      ]);

      const padding = 60 * 60 * 2;
      chart.timeScale().setVisibleRange({
        from: linePoints[0].time - padding,
        to: linePoints[1].time + padding,
      });
    }

    // Tooltip
    const tooltip = document.createElement('div');
    tooltip.style = `
      position: absolute;
      display: none;
      padding: 8px;
      background: rgba(33,33,33,0.9);
      color: #ffffff;
      border-radius: 4px;
      font-size: 12px;
      pointer-events: none;
      z-index: 10;
      border: 1px solid #444;
    `;
    chartContainerRef.current.appendChild(tooltip);

    chart.subscribeCrosshairMove(param => {
      if (!param || !param.time || param.point.x < 0 || param.point.y < 0) {
        tooltip.style.display = 'none';
        return;
      }

      const data = param.seriesData.get(candleSeries);
      if (!data) {
        tooltip.style.display = 'none';
        return;
      }

      const { open, high, low, close } = data;
      const timestamp = new Date(param.time * 1000).toISOString().replace('T', ' ').split('.')[0];

      tooltip.innerHTML = `
        <div><strong>Open:</strong> ${open}</div>
        <div><strong>High:</strong> ${high}</div>
        <div><strong>Low:</strong> ${low}</div>
        <div><strong>Close:</strong> ${close}</div>
        <div><strong>Time:</strong> ${timestamp}</div>
      `;

      tooltip.style.left = param.point.x + 15 + 'px';
      tooltip.style.top = param.point.y + 15 + 'px';
      tooltip.style.display = 'block';

      const hoveredTime = param.time;

      if (firstPoint && hoveredTime === firstPoint.time) {
        setPositionInfo(prev => ({
          ...prev,
          first: true,
          second: false
        }));
      }

      if (linePoints?.length === 2) {
        if (hoveredTime === linePoints[0].time) {
          setPositionInfo(prev => ({
            ...prev,
            first: true,
            second: false
          }));
        }
        if (hoveredTime === linePoints[1].time) {
          setPositionInfo(prev => ({
            ...prev,
            first: false,
            second: true
          }));
        }
      }
    });

    return () => {
      chart?.remove();
      if (tooltip && chartContainerRef?.current?.contains(tooltip)) {
        chartContainerRef.current.removeChild(tooltip);
      }
    };
  }, [data, selectedDate, chartLoading]);

  return (
    chartLoading ? (
      <div>
        <Loader2 open={chartLoading} />
        <div
          ref={chartContainerRef}
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            backgroundColor: '#1e1e1e',
          }}
        />
      </div>
    ) : (
      <div
        ref={chartContainerRef}
        style={{
          width: '100%',
          position: 'relative',
          backgroundColor: '#1e1e1e',
        }}
      />
    )
  );
};

export default CandleStickChart;
